import type { TableProps } from "antd";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomTable } from "sipli-fleet-core/components";
import { useQueryGetAuditLog } from "src/queries/auditLog.queries";
import type {
  AuditLogCategory,
  AuditLogRecord,
} from "src/services/auditLogService";
import { cn } from "src/util/cn";
import CustomEmpty from "../../../../../sipli-fleet-core/components/CustomEmpty/CustomEmpty";

interface AuditLogTabProps {
  vehicleId?: string | number;
}

const DEFAULT_PAGINATION = {
  page: 0,
  size: 10,
};

const getAuditLogCategoryKey = (value?: string | null) =>
  value ? `audit_log_category_${value.trim().toLowerCase()}` : "";

const AUDIT_LOG_CATEGORIES: Array<{
  value: AuditLogCategory;
  labelKey: string;
}> = [
  { value: "DEVICE", labelKey: "device" },
  { value: "UNIT", labelKey: "unit" },
  { value: "RELAY", labelKey: "relay" },
  { value: "USER", labelKey: "user" },
  { value: "GEOFENCE", labelKey: "geofence" },
];

const formatTimestamp = (value: AuditLogRecord["created_at"]) => {
  if (!value) return { date: "--", time: "" };

  const date = dayjs(value);
  return date.isValid()
    ? { date: date.format("YYYY-MM-DD"), time: date.format("HH:mm") }
    : { date: "Invalid Date", time: "" };
};

const AuditLogTab = ({ vehicleId }: AuditLogTabProps) => {
  const { t } = useTranslation();
  const [category, setCategory] = useState<AuditLogCategory>("DEVICE");
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  // const deviceInfoQuery = useQueryDeviceInfo(unitId, category === "DEVICE");
  // const deviceId = deviceInfoQuery.data?.device?.id;

  // Previous conditional targetId:
  // const targetId = useMemo(() => {
  //   if (category === "DEVICE") return deviceId;
  //   if (category === "UNIT") return unitId;
  //   if (category === "RELAY") return vehicleId;
  //   return null;
  // }, [category, deviceId, unitId, vehicleId]);
  const auditLogQuery = useQueryGetAuditLog({
    size: pagination.size,
    page: pagination.page,
    category,
    vehicle_id: vehicleId,
  });

  const logs = auditLogQuery.data?.data.events ?? [];
  const totalCount = auditLogQuery.data?.data.pagination.total_elements ?? 0;

  const columns: TableProps<AuditLogRecord>["columns"] = useMemo(
    () => [
      {
        title: t("ID"),
        dataIndex: "event_id",
        key: "event_id",
        width: 120,
        render: (value) => (
          <span className="text-[14px] font-[600] leading-[18px] text-black">{value ?? "--"}</span>
        ),
      },
      {
        title: t("deviceId"),
        dataIndex: "vehicle_id",
        key: "vehicle_id",
        width: 130,
        render: (value, record) => (
          <span className="text-[14px] font-[600] leading-[18px] text-black">
            {value ?? record.target_id ?? "--"}
          </span>
        ),
      },
      {
        title: t("username"),
        dataIndex: "username",
        key: "username",
        width: 150,
        render: (value) => (
          <span className="text-[14px] font-[600] leading-[18px] text-black">{value || "--"}</span>
        ),
      },
      {
        title: t("category"),
        dataIndex: "category",
        key: "category",
        width: 150,
        render: (value) => (
          <span className="text-[14px] font-[600] leading-[18px] text-black">
            {value ? t(getAuditLogCategoryKey(value), { defaultValue: value }) : "--"}
          </span>
        ),
      },
      {
        title: t("operation"),
        dataIndex: "operation",
        key: "operation",
        width: 250,
        render: (value) => (
          <span className="text-[14px] font-[400] leading-[18px] text-gray9">
            {value ? t(value.trim(), { defaultValue: value }) : "--"}
          </span>
        ),
      },
      {
        title: t("description"),
        dataIndex: "description",
        key: "description",
        width: 420,
        render: (value) => (
          <div
            className="min-w-[360px] break-words text-[14px] font-[600] leading-[18px] text-black [&_p]:m-0"
            dangerouslySetInnerHTML={{ __html: value || "--" }}
          />
        ),
      },
      {
        title: t("timestamp"),
        dataIndex: "created_at",
        key: "created_at",
        width: 200,
        render: (value) => {
          const timestamp = formatTimestamp(value);

          return (
            <div className="whitespace-nowrap text-[14px] leading-[18px] text-black">
              <div className="font-[600]">{timestamp.date}</div>
              {timestamp.time && <div className="font-[400]">{timestamp.time}</div>}
            </div>
          );
        },
      },
    ],
    [t]
  );

  const handleCategoryChange = (value: AuditLogCategory) => {
    if (value === category) return;

    setCategory(value);
    setPagination(DEFAULT_PAGINATION);
  };

  return (
    <div className="rounded-[16px] bg-white pt-5 pb-6 pl-[18px] pr-[15px]">
      <h2 className="m-0 text-[19.93px] font-[700] leading-[28px] text-black">{t("audit_log")}</h2>

      <div className="mt-8 flex flex-col items-start gap-4 xl:flex-row xl:gap-5">
        <aside className="flex shrink-0 flex-row gap-1.5 overflow-x-auto xl:w-[104px] xl:flex-col xl:overflow-visible">
          {AUDIT_LOG_CATEGORIES.map((item) => {
            const isActive = category === item.value;

            return (
              <button
                key={item.value}
                type="button"
                aria-pressed={isActive}
                className={cn(
                  "h-[38px] w-full min-w-max shrink-0 cursor-pointer whitespace-nowrap rounded-none px-2 text-left text-[14px] font-semibold leading-[18px] transition-colors xl:w-[104px] xl:min-w-[104px]",
                  isActive ? "bg-primary6 text-primary" : "bg-white text-gray9 hover:bg-gray1"
                )}
                onClick={() => handleCategoryChange(item.value)}
              >
                {t(item.labelKey)}
              </button>
            );
          })}
        </aside>

        <CustomTable<AuditLogRecord>
          customClass="customers-table audit-log-table min-w-0 w-full xl:flex-1 [&_.ant-table-thead>tr>th]:!text-left [&_.ant-table-thead>tr>th]:!text-[14px] [&_.ant-table-thead>tr>th]:!font-[600] [&_.ant-table-thead>tr>th]:!leading-[18px] [&_.ant-table-tbody>tr>td]:!text-left [&_.ant-table-tbody>tr>td]:!py-4"
          data={logs}
          columns={columns}
          rowKey={(record) => record.event_id}
          isLoading={auditLogQuery.isLoading || auditLogQuery.isFetching}
          pageSize={pagination.size}
          currentPage={pagination.page}
          totalCount={totalCount}
          handlePagination={(page) => setPagination((previous) => ({ ...previous, page }))}
          handlePageSizeChange={(size) => setPagination({ page: 0, size: Number(size) })}
          showSearchInput={false}
          showCustomizeColumns={false}
          showDownload={false}
          scroll={{ x: logs?.length ? 1400 : undefined }}
          locale={{ emptyText: <CustomEmpty /> }}
        />
      </div>
    </div>
  );
};

export default AuditLogTab;
